import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type CartSummaryRow = {
  userEmail: string;
  username: string;
  totalItems: number;
  totalAmount: number;
  updatedAt?: string;
};

type CartItemRow = {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  stock: number;
  isActive: boolean;
};

type CartDetails = {
  userEmail: string;
  totalItems: number;
  totalAmount: number;
  items: CartItemRow[];
};

const Carts = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<CartSummaryRow[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState<'updatedAt' | 'totalItems' | 'totalAmount' | 'email'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [selectedEmail, setSelectedEmail] = useState('');
  const [details, setDetails] = useState<CartDetails | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page,
      limit,
      sortBy,
      sortOrder,
    };
    if (search.trim()) {
      params.search = search.trim();
    }
    return params;
  }, [page, limit, sortBy, sortOrder, search]);

  const fetchCarts = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/cart/admin', {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams,
      });

      const list = Array.isArray(response.data?.carts) ? response.data.carts : [];
      setRows(list);
      setTotal(typeof response.data?.total === 'number' ? response.data.total : list.length);
      setTotalPages(typeof response.data?.totalPages === 'number' ? response.data.totalPages : 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch carts.');
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [token, queryParams]);

  useEffect(() => {
    fetchCarts();
  }, [fetchCarts]);

  useEffect(() => {
    if (!error && !success) return;
    const timer = setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, success]);

  const openDetails = async (email: string) => {
    if (!token) return;

    setSelectedEmail(email);
    try {
      const response = await axios.get('/cart/admin/details', {
        headers: { Authorization: `Bearer ${token}` },
        params: { email },
      });
      setDetails(response.data);
      setShowDetails(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load cart details.');
    }
  };

  const canGoPrev = page > 1;
  const canGoNext = totalPages > 0 && page < totalPages;

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Customer Carts</h4>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form
          className="d-flex gap-2 flex-grow-1"
          style={{ minWidth: '260px' }}
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(searchInput);
          }}
        >
          <Form.Control
            placeholder="Search by email or username"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Search carts"
          />
          <Button type="submit" variant="outline-dark">Search</Button>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
          >
            Clear
          </Button>
        </Form>

        <Form.Select value={sortBy} onChange={(event) => { setSortBy(event.target.value as any); setPage(1); }} style={{ maxWidth: '180px' }}>
          <option value="updatedAt">Sort: Updated</option>
          <option value="totalItems">Sort: Items</option>
          <option value="totalAmount">Sort: Amount</option>
          <option value="email">Sort: Email</option>
        </Form.Select>

        <Form.Select value={sortOrder} onChange={(event) => { setSortOrder(event.target.value as 'asc' | 'desc'); setPage(1); }} style={{ maxWidth: '120px' }}>
          <option value="desc">Desc</option>
          <option value="asc">Asc</option>
        </Form.Select>

        <Form.Select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} style={{ maxWidth: '130px' }}>
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </Form.Select>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Email</th>
            <th>Username</th>
            <th>Total Items</th>
            <th>Total Amount</th>
            <th>Updated At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {!loading && rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center">No carts found.</td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row.userEmail}>
                <td>{(page - 1) * limit + index + 1}</td>
                <td>{row.userEmail}</td>
                <td>{row.username || '-'}</td>
                <td>{row.totalItems}</td>
                <td>Rs {Number(row.totalAmount || 0).toFixed(2)}</td>
                <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                <td>
                  <Button size="sm" variant="dark" onClick={() => openDetails(row.userEmail)}>View Items</Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <small>Showing {rows.length} of {total} carts</small>
        <div className="d-flex align-items-center gap-2">
          <Button size="sm" variant="outline-secondary" disabled={!canGoPrev} onClick={() => setPage((prev) => prev - 1)}>Prev</Button>
          <span>Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
          <Button size="sm" variant="outline-secondary" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>Next</Button>
        </div>
      </div>

      <Modal show={showDetails} onHide={() => setShowDetails(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cart Details {selectedEmail ? `- ${selectedEmail}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!details || details.items.length === 0 ? (
            <p>No cart items found.</p>
          ) : (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Subtotal</th>
                  <th>Stock</th>
                </tr>
              </thead>
              <tbody>
                {details.items.map((item) => (
                  <tr key={item.productId}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>Rs {Number(item.unitPrice).toFixed(2)}</td>
                    <td>Rs {Number(item.subtotal).toFixed(2)}</td>
                    <td>{item.stock}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="fw-bold">Total: Rs {Number(details?.totalAmount || 0).toFixed(2)}</div>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default Carts;
